/**
 * MobileAppBanner — promo banner shown on public pages encouraging mobile
 * visitors to open the TourismIntel iOS / Android app instead of the web.
 *
 * Visibility rules (all must pass):
 *  1. Device is a phone (iOS or Android) — desktops never see it.
 *  2. Not inside a known in-app browser webview (Instagram, Facebook, LinkedIn,
 *     TikTok) — Universal Links don't fire reliably in those.
 *  3. Not running inside the TourismIntel app itself (the native webview can
 *     set window.__TI_NATIVE = true via injected JS to suppress).
 *  4. Not dismissed in the last 7 days (localStorage timestamp).
 *  5. PWA installed mode is NOT standalone (the app/PWA shouldn't promote itself).
 *
 * Tap behaviour:
 *  - iOS: tapping the primary CTA navigates to /app/open. If the app is
 *    installed and the AASA registers `/app/open`, iOS intercepts and opens
 *    the native app. Otherwise /app/open renders OpenAppPage which redirects
 *    to the App Store after a short fallback timer.
 *  - Android: same pattern — the assetlinks.json + intent filter on
 *    /app/open opens the native app, else the Play Store.
 *
 * The banner is intentionally small (h-14), high-contrast, and dismissible
 * with a visible × button so it never feels intrusive.
 */
import React, { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";

const DISMISS_KEY = "ti_mobile_banner_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const APP_STORE_URL = "https://apps.apple.com/it/app/tourismintel/id6778354345";

function detectShouldShow() {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";

  // Native app suppression hook.
  if (window.__TI_NATIVE === true) return false;

  // PWA standalone mode — already "in an app".
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true;
  if (standalone) return false;

  // Device must be a phone (we don't bother iPad — too few mobile-app users).
  const isIOS = /iPhone|iPod/.test(ua);
  const isAndroidPhone = /Android/.test(ua) && /Mobile/.test(ua);
  if (!isIOS && !isAndroidPhone) return false;

  // In-app browsers (Universal Links + App Links don't fire reliably here).
  const inAppPatterns = [
    "FBAN",
    "FBAV",
    "Instagram",
    "LinkedInApp",
    "Twitter",
    "TikTok",
    "Line/",
    "MicroMessenger", // WeChat
    "Snapchat",
  ];
  if (inAppPatterns.some((p) => ua.includes(p))) return false;

  // Dismissed recently?
  try {
    const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (at && Date.now() - at < DISMISS_TTL_MS) return false;
  } catch {
    /* localStorage blocked — keep showing */
  }

  return true;
}

export default function MobileAppBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer the visibility check to the client to avoid SSR/hydration noise.
    setVisible(detectShouldShow());
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="mobile-app-banner"
      className="sticky top-0 z-40 w-full"
      style={{
        background: "linear-gradient(90deg, #006874 0%, #0a8a99 100%)",
        boxShadow: "0 1px 0 rgba(0,0,0,0.08)",
      }}
    >
      <div className="flex items-center gap-2 px-3 py-2.5 max-w-screen-xl mx-auto">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
          <Smartphone className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[13px] font-semibold leading-tight">
            TourismIntel app
          </p>
          <p className="text-white/80 text-[11px] leading-tight">
            Daily audio briefings, offline & push.
          </p>
        </div>
        <a
          href="/app/open"
          data-testid="mobile-app-banner-cta"
          className="flex-shrink-0 inline-flex items-center justify-center px-3 py-1.5 rounded-full text-[12px] font-semibold text-[#003a40] bg-white hover:bg-white/90 transition-colors"
          // App Store fallback when the app isn't installed AND the UA isn't iOS.
          // iOS Universal Link intercepts /app/open before this attr matters.
          rel="noopener"
        >
          Open
        </a>
        <a
          href={APP_STORE_URL}
          data-testid="mobile-app-banner-store"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex flex-shrink-0 items-center text-white/85 hover:text-white text-[11px] underline-offset-2 hover:underline"
        >
          App Store
        </a>
        <button
          type="button"
          onClick={dismiss}
          data-testid="mobile-app-banner-dismiss"
          aria-label="Close banner"
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
